import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  Collapse,
  Divider,
  LinearProgress,
  Avatar,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import InlineEdit from './Inlineedit.tsx';
import WeekItem from './Weekitem.tsx';
import { Topic, Week, Subtopic, Day  } from '../../../types/curriculum.types.ts';

interface TopicItemProps {
  topic: Topic;
  topicIndex: number;
  onUpdateTopic: (id: string, patch: Partial<Topic>) => void;
  onDeleteTopic: (id: string) => void;
  onAddWeek: (topicId: string) => void;
  onUpdateWeek: (topicId: string, weekId: string, patch: Partial<Week>) => void;
  onDeleteWeek: (topicId: string, weekId: string) => void;
  onUpdateWeekTopic: (topicId: string, weekId: string, newTopicId: string) => void;
  onAddSubtopic: (topicId: string, weekId: string) => void;
  onUpdateSubtopic: (topicId: string, weekId: string, subtopicId: string, patch: Partial<Subtopic>) => void;
  onDeleteSubtopic: (topicId: string, weekId: string, subtopicId: string) => void;
  onAddDay: (topicId: string, weekId: string) => void;
  onUpdateDay: (topicId: string, weekId: string, dayId: string, patch: Partial<Day>) => void;
  onDeleteDay: (topicId: string, weekId: string, dayId: string) => void;
}

export default function TopicItem({
  topic,
  topicIndex,
  onUpdateTopic,
  onDeleteTopic,
  onAddWeek,
  onUpdateWeek,
  onDeleteWeek,
  onUpdateWeekTopic,
  onAddSubtopic,
  onUpdateSubtopic,
  onDeleteSubtopic,
  onAddDay,
  onUpdateDay,
  onDeleteDay,
}: TopicItemProps) {
  const [expanded, setExpanded] = useState(true);

  const weeksAdded = topic.weeks.length;
  const progress = Math.round((weeksAdded / topic.duration) * 100);
  const totalMarks = topic.weeks.reduce((sum, w) => sum + w.subtopics.reduce((s, st) => s + (st.marks || 0), 0), 0);
  const weeksFulfilled = weeksAdded === topic.duration;
  const totalDays = topic.weeks.reduce((sum, w) => sum + w.days.length, 0);
  const totalHolidays = topic.weeks.reduce((sum, w) => sum + w.days.filter(d => d.isHoliday).length, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid #E2E8F0',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'white',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', flexWrap: 'wrap' }}>
        <Avatar sx={{ width: 28, height: 28, bgcolor: '#3B82F6', fontSize: '0.75rem' }}>{topicIndex + 1}</Avatar>
        <InlineEdit value={topic.name} onSave={(v) => onUpdateTopic(topic.id, { name: v })} variant="h6" bold placeholder="Topic name" fontSize="1rem" />
        <Chip icon={<TimerIcon sx={{ fontSize: 16 }} />} label={`${topic.duration} weeks`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#DBEAFE', color: '#1E40AF' }} />
        <Chip
          icon={weeksFulfilled ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : <PendingIcon sx={{ fontSize: 16 }} />}
          label={`${weeksAdded}/${topic.duration}`}
          size="small"
          sx={{ fontSize: '0.65rem', height: 22, bgcolor: weeksFulfilled ? '#D1FAE5' : '#FEF3C7', color: weeksFulfilled ? '#065F46' : '#92400E' }}
        />
        <Chip label={`${totalDays} days`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#E0E7FF', color: '#3730A3' }} />
        {totalHolidays > 0 && (
          <Chip label={`${totalHolidays} holidays`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEE2E2', color: '#991B1B' }} />
        )}
        <Chip label={`${totalMarks} marks`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E' }} />
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow>
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 22 }} /> : <ExpandMoreIcon sx={{ fontSize: 22 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Topic" arrow>
            <IconButton size="small" color="error" onClick={() => onDeleteTopic(topic.id)} sx={{ p: 0.5 }}>
              <DeleteIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ px: 2.5, pt: 0.5, pb: 0 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(progress, 100)}
          sx={{
            height: 4, borderRadius: 2, bgcolor: '#E2E8F0',
            '& .MuiLinearProgress-bar': { bgcolor: progress === 100 ? '#22C55E' : '#3B82F6', borderRadius: 2 },
          }}
        />
      </Box>

      {/* Description */}
      <Box sx={{ px: 2.5, py: 0.5 }}>
        <InlineEdit value={topic.description || ''} onSave={(v) => onUpdateTopic(topic.id, { description: v })} variant="body2" placeholder="Add description..." fontSize="0.85rem" />
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.9rem' }}>
              Weeks ({weeksAdded}/{topic.duration})
            </Typography>
            <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => onAddWeek(topic.id)}
              sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5, minHeight: 32 }}
            >Add Week</Button>
          </Box>

          {topic.weeks.length === 0 && (
            <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.85rem' }}>No weeks added</Typography>
          )}

          {topic.weeks.map((week, wi) => (
            <WeekItem
              key={week.id}
              week={week}
              weekIndex={wi}
              availableTopics={[topic]}
              onUpdate={(id, patch) => onUpdateWeek(topic.id, id, patch)}
              onDelete={(id) => onDeleteWeek(topic.id, id)}
              onUpdateTopic={(weekId, newTopicId) => onUpdateWeekTopic(topic.id, weekId, newTopicId)}
              onAddSubtopic={(weekId) => onAddSubtopic(topic.id, weekId)}
              onUpdateSubtopic={(weekId, subtopicId, patch) => onUpdateSubtopic(topic.id, weekId, subtopicId, patch)}
              onDeleteSubtopic={(weekId, subtopicId) => onDeleteSubtopic(topic.id, weekId, subtopicId)}
              onAddDay={(weekId) => onAddDay(topic.id, weekId)}
              onUpdateDay={(weekId, dayId, patch) => onUpdateDay(topic.id, weekId, dayId, patch)}
              onDeleteDay={(weekId, dayId) => onDeleteDay(topic.id, weekId, dayId)}
            />
          ))}

          {weeksAdded < topic.duration && (
            <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.8rem', py: 1 }}>
              {topic.duration - weeksAdded} more week{(topic.duration - weeksAdded) > 1 ? 's' : ''} needed
            </Alert>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}